import { inject, Injectable } from '@angular/core';
import { EntitlementsService } from 'app/core/entitlements/entitlements.service';
import {
    AlturaNavigationItem,
    NAVIGATION,
    NAVIGATION_MOBILE,
} from 'app/core/navigation/navigation.data';
import { Navigation } from 'app/core/navigation/navigation.types';
import { Observable, ReplaySubject, of, tap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class NavigationService {
    private _entitlements = inject(EntitlementsService);
    private _navigation: ReplaySubject<Navigation> =
        new ReplaySubject<Navigation>(1);

    get navigation$(): Observable<Navigation> {
        return this._navigation.asObservable();
    }

    /**
     * Build the menu for the current plan.
     *
     * Still an Observable so `initialDataResolver` keeps working unchanged,
     * but it no longer makes an HTTP call — the menu is app structure, not
     * server data.
     */
    get(): Observable<Navigation> {
        const items = this.visible(NAVIGATION);
        const mobile = this.visible(NAVIGATION_MOBILE);

        return of({
            compact: items,
            default: items,
            futuristic: items,
            horizontal: mobile,
        } as Navigation).pipe(
            tap((navigation) => this._navigation.next(navigation))
        );
    }

    /** Recompute after the plan changes, e.g. straight after an upgrade. */
    refresh(): void {
        this.get().subscribe();
    }

    /**
     * Items the current plan can reach.
     *
     * A locked module stays in the menu, dimmed — owners should see the shape
     * of the whole product, not a menu that silently grows when they pay. Items
     * belonging to the other operating rule are dropped outright, because a
     * Part 91 owner should never learn the word "quote".
     */
    private visible(items: AlturaNavigationItem[]): AlturaNavigationItem[] {
        const { planCode, operatingRule } = this._entitlements.entitlements();
        // Before a plan is known, show everything. Hiding a module on a guess
        // would make the menu flicker for users whose plan is still loading,
        // and rule-specific modules are dropped outright — an item that
        // vanishes and returns is worse than one that was always there.
        const ruleKnown = planCode !== 'unknown';

        return items
            .filter((item) => !ruleKnown || !item.onlyForRule || item.onlyForRule === operatingRule)
            .map((item) => {
                const locked = !!item.requires && !this._entitlements.allows(item.requires);
                return locked
                    ? { ...item, classes: { wrapper: 'altura-nav-locked' } }
                    : item;
            });
    }
}
